import { useSelector, useDispatch } from 'react-redux';
import { useCallback, useEffect } from 'react';
import { setCurrentMode, selectCurrentMode, selectIsLawyer } from './slices/authSlice';

// Hook para autenticación
export const useAuth = () => {
  const dispatch = useDispatch();
  const auth = useSelector(state => state.auth);

  const login = useCallback(
    (email, password, userType) => {
      return dispatch({ type: 'auth/loginUser', payload: { email, password, userType } });
    },
    [dispatch]
  );

  const logout = useCallback(() => {
    return dispatch({ type: 'auth/logoutUser' });
  }, [dispatch]);

  const clearError = useCallback(() => {
    dispatch({ type: 'auth/clearError' });
  }, [dispatch]);

  return {
    ...auth,
    login,
    logout,
    clearError,
  };
};

// Hook para UI
export const useUI = () => {
  const dispatch = useDispatch();
  const ui = useSelector(state => state.ui);

  const toggleSideMenu = useCallback(() => {
    dispatch({ type: 'ui/toggleSideMenu' });
  }, [dispatch]);

  const openSideMenu = useCallback(() => {
    dispatch({ type: 'ui/openSideMenu' });
  }, [dispatch]);

  const closeSideMenu = useCallback(() => {
    dispatch({ type: 'ui/closeSideMenu' });
  }, [dispatch]);

  const setTheme = useCallback(
    theme => {
      dispatch({ type: 'ui/setTheme', payload: theme });
    },
    [dispatch]
  );

  const setUserTypeTheme = useCallback(
    userType => {
      dispatch({ type: 'ui/setUserTypeTheme', payload: userType });
    },
    [dispatch]
  );

  const addNotification = useCallback(
    notification => {
      dispatch({ type: 'ui/addNotification', payload: notification });
    },
    [dispatch]
  );

  const addAlert = useCallback(
    alert => {
      dispatch({ type: 'ui/addAlert', payload: alert });
    },
    [dispatch]
  );

  const setIsInHeroSection = useCallback(
    isInHero => {
      dispatch({ type: 'ui/setIsInHeroSection', payload: isInHero });
    },
    [dispatch]
  );

  return {
    ...ui,
    toggleSideMenu,
    openSideMenu,
    closeSideMenu,
    setTheme,
    setUserTypeTheme,
    addNotification,
    addAlert,
    setIsInHeroSection,
  };
};

// Hook para formularios
export const useForm = formType => {
  const dispatch = useDispatch();
  const forms = useSelector(state => state.forms);

  const currentForm = forms[`${formType}Form`];

  const updateField = useCallback(
    (field, value) => {
      dispatch({
        type: `forms/update${formType.charAt(0).toUpperCase() + formType.slice(1)}FormField`,
        payload: { field, value },
      });
    },
    [dispatch, formType]
  );

  const setFieldError = useCallback(
    (field, error) => {
      dispatch({
        type: `forms/set${formType.charAt(0).toUpperCase() + formType.slice(1)}FormError`,
        payload: { field, error },
      });
    },
    [dispatch, formType]
  );

  const clearForm = useCallback(() => {
    dispatch({ type: `forms/clear${formType.charAt(0).toUpperCase() + formType.slice(1)}Form` });
  }, [dispatch, formType]);

  const validateForm = useCallback(() => {
    dispatch({ type: `forms/validate${formType.charAt(0).toUpperCase() + formType.slice(1)}Form` });
  }, [dispatch, formType]);

  return {
    ...currentForm,
    updateField,
    setFieldError,
    clearForm,
    validateForm,
  };
};

// Hook para datos de API
export const useApiData = () => {
  const dispatch = useDispatch();
  const api = useSelector(state => state.api);

  const initializeData = useCallback(() => {
    dispatch({ type: 'api/initializeApiData' });
  }, [dispatch]);

  const fetchGenders = useCallback(() => {
    dispatch({ type: 'api/fetchGenders' });
  }, [dispatch]);

  const fetchOccupations = useCallback(() => {
    dispatch({ type: 'api/fetchOccupations' });
  }, [dispatch]);

  const fetchSpecialties = useCallback(() => {
    dispatch({ type: 'api/fetchSpecialties' });
  }, [dispatch]);

  return {
    ...api,
    initializeData,
    fetchGenders,
    fetchOccupations,
    fetchSpecialties,
  };
};

// Hook para restablecer estado desde localStorage (hydration)
export const useStateHydration = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    // Restaurar estado de autenticación desde localStorage
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const parsedUser = JSON.parse(userData);
        dispatch({ type: 'auth/restoreAuth', payload: parsedUser });

        // Establecer tema basado en tipo de usuario
        if (parsedUser.type) {
          dispatch({ type: 'ui/setUserTypeTheme', payload: parsedUser.type });
        }
      } catch (error) {
        console.error('Error parsing user data from localStorage:', error);
        localStorage.removeItem('user'); // Limpiar datos corruptos
      }
    }
  }, [dispatch]);
};

// Hook para detectar cambios sin guardar
export const useUnsavedChanges = () => {
  const hasUnsavedChanges = useSelector(
    state => state.forms.clientForm.isDirty || state.forms.lawyerForm.isDirty
  );

  useEffect(() => {
    const handleBeforeUnload = event => {
      if (hasUnsavedChanges) {
        event.preventDefault();
        event.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  return hasUnsavedChanges;
};

// Hook para auto-guardar formularios
export const useAutoSave = (formType, interval = 30000) => {
  // 30 segundos
  const dispatch = useDispatch();
  const form = useSelector(state => state.forms[`${formType}Form`]);
  const autoSaveEnabled = useSelector(state => state.forms.formMetadata.autoSaveEnabled);

  useEffect(() => {
    if (!autoSaveEnabled || !form.isDirty) return;

    const autoSaveTimer = setInterval(() => {
      if (form.isDirty && Object.keys(form.errors).length === 0) {
        // Solo auto-guardar si no hay errores
        dispatch({ type: 'forms/markFormAsSaved', payload: formType });
        console.log(`Auto-saved ${formType} form`);
      }
    }, interval);

    return () => clearInterval(autoSaveTimer);
  }, [dispatch, form.isDirty, form.errors, formType, interval, autoSaveEnabled]);
};

// Hook para notificaciones automáticas
export const useNotifications = () => {
  const dispatch = useDispatch();
  const alerts = useSelector(state => state.ui.alerts);

  const showSuccess = useCallback(
    (message, options = {}) => {
      dispatch({
        type: 'ui/addAlert',
        payload: {
          type: 'success',
          message,
          ...options,
        },
      });
    },
    [dispatch]
  );

  const showError = useCallback(
    (message, options = {}) => {
      dispatch({
        type: 'ui/addAlert',
        payload: {
          type: 'error',
          message,
          autoRemove: false, // Los errores no se quitan automáticamente
          ...options,
        },
      });
    },
    [dispatch]
  );

  const showWarning = useCallback(
    (message, options = {}) => {
      dispatch({
        type: 'ui/addAlert',
        payload: {
          type: 'warning',
          message,
          ...options,
        },
      });
    },
    [dispatch]
  );

  const showInfo = useCallback(
    (message, options = {}) => {
      dispatch({
        type: 'ui/addAlert',
        payload: {
          type: 'info',
          message,
          ...options,
        },
      });
    },
    [dispatch]
  );

  const removeAlert = useCallback(
    alertId => {
      dispatch({ type: 'ui/removeAlert', payload: alertId });
    },
    [dispatch]
  );

  // Auto-remove alerts
  useEffect(() => {
    alerts.forEach(alert => {
      if (alert.autoRemove) {
        const timer = setTimeout(() => {
          removeAlert(alert.id);
        }, alert.duration);

        return () => clearTimeout(timer);
      }
    });
  }, [alerts, removeAlert]);

  return {
    alerts,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    removeAlert,
  };
};

// Hook para el modo de autenticación (cliente/abogado)
export const useAuthMode = () => {
  const dispatch = useDispatch();
  const currentMode = useSelector(selectCurrentMode);
  const isLawyer = useSelector(selectIsLawyer);

  const setMode = useCallback(
    mode => {
      dispatch(setCurrentMode(mode));
    },
    [dispatch]
  );

  const toggleMode = useCallback(() => {
    dispatch(setCurrentMode(isLawyer ? 'client' : 'lawyer'));
  }, [dispatch, isLawyer]);

  return {
    currentMode,
    isLawyer,
    isClient: !isLawyer,
    setMode,
    toggleMode,
    setClientMode: () => setMode('client'),
    setLawyerMode: () => setMode('lawyer'),
  };
};
